import { CommonModule } from "@angular/common";
import {
  AfterViewInit,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
} from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { AgGridAngular, AgGridModule } from "ag-grid-angular";
import {
  ColDef,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  IRowNode,
  IsRowMaster,
  RowDropZoneParams,
  RowHeightParams,
  RowNode,
} from "ag-grid-community";
import { map, Observable } from "rxjs";
import { DropdownCellEditor } from "../../components/core/table/cell-editors/dropdown-cell-editor.component";
import { InputCellEditor } from "../../components/core/table/cell-editors/input-cell-editor.component";
import { MyInnerRenderer } from "../../components/core/table/totals-row-renderer.component";
import { TableVariant } from "../../enums/table-variant";
import { DropzoneService } from "./dropzone.service";
import { GridService } from "./grid.service";
import { DropdownComponent } from "../../components/core/dropdown/dropdown.component";
import { FormsModule } from "@angular/forms";
import { GRID_DOM_LAYOUT } from "../../constants/grid-dom-layout";
import { ExpandIconRendererComponent } from "./components/expand-icon-renderer.component";

@UntilDestroy()
@Component({
  selector: "thera-client-side-list",
  template: `
    <div
      style="height: 100%; box-sizing: border-box; border: none!important"
      #gridDiv
    >
      <ag-grid-angular
        #grid
        [autoGroupColumnDef]="autoGroupColumnDef"
        [class.no-grid]="hideGridLines"
        style="width: 100%; height: 100%"
        class="ag-theme-alpine"
        [class]="variant"
        [rowDragText]="rowDragText"
        [columnDefs]="columnDefs"
        [suppressCellFocus]="true"
        [detailCellRenderer]="detailCellRenderer"
        [context]="gridContext"
        [groupIncludeTotalFooter]="hasTotalRow"
        [domLayout]="layout"
        [rowDragManaged]="true"
        [suppressMoveWhenRowDragging]="true"
        [suppressDragLeaveHidesColumns]="true"
        [suppressRowDeselection]="suppressRowDeselection"
        [masterDetail]="detailRowsEnabled"
        [suppressRowClickSelection]="suppressRowClickSelection"
        [animateRows]="animateRows"
        [isRowMaster]="isRowMaster"
        [detailCellRendererParams]="detailRowRenderParams"
        (rowClicked)="onRowClicked($event)"
        [rowData]="gridRowData"
        (gridReady)="onGridReady($event)"
        (selectionChanged)="onSelectionChanged($event)"
        (rowSelected)="onRowSelected($event)"
        [rowSelection]="rowSelection"
        [overlayNoRowsTemplate]="noRowsTemplate"
        [getRowStyle]="getRowStyle"
        (sortChanged)="sortChange($event)"
        [rowMultiSelectWithClick]="rowMultiSelectWithClick"
        [detailRowAutoHeight]="true"
        [getRowClass]="getRowClass"
        [rowClassRules]="rowClassRules"
        [getRowId]="getRowId"
      ></ag-grid-angular>

      <div
        *ngIf="dropZoneId"
        [attr.unique-identifier]="gridId"
        [attr.id]="dropZoneId"
        class="mt-2 text-center bg-[#f2f2f28c] drop-container border-dashed border-2 border-[#e0e0e0] rounded-md p-4"
      >
        <span class="font-normal"> Drag and Drop Facilities</span>
      </div>

      <div
        class="mt-3 flex custom-dropdown-container"
        *ngIf="totalPages > 0 && enablePagination"
      >
        <div class="pageSize text-[#697586]">
          <span class="mr-4">Showing</span>
          <thera-dropdown
            class="mr-2 w-3"
            (optionSelected)="onPageSizeChange($event)"
            [options]="pagesSizes"
            [defaultSelection]="pageSize"
          ></thera-dropdown>
          <span class="mr-4">of {{ totalRecords }} results</span>
        </div>
        <ng-container>
          <div class="flex justify-between ml-auto">
            <div class="flex mt-3 gap-2 font-inter">
              <span class="mr-4">
                <input
                  class="input-wrapper"
                  type="number"
                  [(ngModel)]="currentPage"
                  (blur)="onPageClick($event)"
                  (keydown)="onBlur($event)"
                />
                of {{ totalPages }}
              </span>
              <div
                class="chivron-icon"
                (click)="onPreviousPage()"
                [class.disabled]="isFirstPage"
              >
                <i class="fas fa-chevron-left"></i>
              </div>
              <div
                class="chivron-icon"
                (click)="onNextPage()"
                [class.disabled]="isLastPage"
              >
                <i class="fas fa-chevron-right"></i>
              </div>
            </div>
          </div>
        </ng-container>
      </div>
    </div>
  `,
  standalone: true,
  styleUrls: ["./client-side-list-base.component.scss"],
  imports: [
    DropdownCellEditor,
    AgGridModule,
    InputCellEditor,
    CommonModule,
    DropdownComponent,
    FormsModule,
    ExpandIconRendererComponent,
  ],
  providers: [],
})
export class ClientSideListBaseComponent implements AfterViewInit {
  readonly gridId: string = "id" + Math.random().toString(16).slice(2);
  private rowStyle = (params: any) => {
    if (this.variant !== TableVariant.SECONDARY) {
      return;
    }
    if (params.node.rowIndex % 2 === 0) {
      return { background: "#f3f9fd" };
    }

    return { background: "white" };
  };
  private originalRowData: any[] = [];
  getRowStyle = this.rowStyle.bind(this);
  @Input() enablePagination: boolean = false;
  gridRowData: any[] = [];
  @Input() rowSelection: "single" | "multiple" | undefined;
  @Input() detailRowsEnabled = false;
  @Input() detailRowRenderParams: any = {};
  @Input() detailCellRenderer: any = {};
  @Input() columnDefs: ColDef[] = [];
  @Input() hideGridLines = false;
  @Input() newlySelectedIds?: number[];
  @Input() set rowData(event: any) {
    if (this.expandableRows) {
      this.originalRowData = event.map((row: any) => ({
        ...row,
        isRowExpanded: this.expandAllRowsByDefault //expandable by default
      }));
    }
    else {
      this.originalRowData = event
    }
    
    if (this.enablePagination) {
      this.totalRecords = this.originalRowData.length;
      this.totalPages = Math.ceil(this.totalRecords / this.pageSize);
      this.currentPage = 1;
      this.updateGridData();
    } else {
      this.gridRowData = [...this.originalRowData];
    }
    
    setTimeout(() => {
      if (this.selectedRowIds) {
        this.checkCheckboxesById(
          this.selectedRowIds,
          this.propertyForSelectionNodes
        );
      }

      if (this.newlySelectedIds) {
        this.newlyCheckCheckboxesById(
          this.newlySelectedIds,
          this.propertyForSelectionNodes
        );
      }
    }, 100);
  }
  @Input() gridContext: any;
  @Input() animateRows!: boolean;
  @Input() rowDragManaged!: boolean;
  @Input() rowDragText!: any;
  @Input() draggableAndDroppable!: boolean;
  @Input() dropZoneId!: string;
  @Input() resize$!: Observable<void>;
  @Input() selectedRowIndex$?: Observable<number | null>;
  @Input() variant: TableVariant = TableVariant.PRIMARY;
  @Input() hasTotalRow: boolean = false;
  @Input() rowHeight: number = 55;
  @Input() suppressRowClickSelection: boolean = false;
  @Input() suppressRowDeselection: boolean = false;
  @Input() noRowsTemplate: string = "No results found";
  @Input() filterUpdated$?: Observable<string>;
  @Input() refreshCells$?: Observable<void>;
  @Input() isRowMaster?: IsRowMaster;
  @Input() resetSelections$?: Observable<void>;
  @Input() selectedRowIds?: number[];
  @Input() propertyForSelectionNodes?: any;
  @Input() rowMultiSelectWithClick: boolean = false;
  @Input() bypassAutoHeight = false;
  @Input() getRowClass?: (params: any) => string | string[];
  @Input() layout = GRID_DOM_LAYOUT.AUTO_HEIGHT;
  @Input() getRowId?: (params: GetRowIdParams) => string;
  @Input() expandableRows = false;

  private _expandAllRowsByDefault = false;

  @Input()
  set expandAllRowsByDefault(value: boolean) {
    this._expandAllRowsByDefault = this.expandableRows ? value : false;
  }

  get expandAllRowsByDefault() {
    return this._expandAllRowsByDefault;
  }

  @Output() rowAdded = new EventEmitter();
  @Output() selectionChanged = new EventEmitter();
  @Output() rowChecked = new EventEmitter();
  @Output() refreshGridData = new EventEmitter();
  @Output() gridReady = new EventEmitter();
  @Output() onClickedRow = new EventEmitter();
  protected gridApi!: GridApi;
  params: any;
  @ViewChild("grid") agGrid!: AgGridAngular;
  @ViewChild("gridDiv") gridDiv!: HTMLDivElement;
  @Output() dataLoaded = new EventEmitter<GridApi>();
  selectedRowIndex = 0;

  dropzoneService = inject(DropzoneService);
  gridService = inject(GridService);

  currentPage: number = 1;
  totalPages: number = 0;
  @Input() pageSize: number = 20;
  totalRecords: number = 0;
  pagesSizes: { name: string; id: string }[] = [
    { name: "5", id: "5" },
    { name: "10", id: "10" },
    { name: "20", id: "20" },
    { name: "50", id: "50" },
    { name: "75", id: "75" },
    { name: "100", id: "100" },
  ];

  get isFirstPage(): boolean {
    return this.currentPage === 1;
  }
  get isLastPage(): boolean {
    return this.currentPage === this.totalPages;
  }

  rowClassRules = {
    'row-expanded': (params: any) => params.data?.isRowExpanded === true,
  };

  ngAfterViewInit(): void {
    if (!this.columnDefs) {
      return;
    }

    if (this.expandableRows) {
      this.configureColumnsWithExpandBehavior();
    }

    this.columnDefs = this.columnDefs.map((colDef) => {
      return {
        ...colDef,
        suppressMenu: true,
      };
    });
  }

  onSelectionChanged(event: any) {
    this.selectionChanged.emit(event);
  }

  onRowSelected(event: any) {
    this.rowChecked.emit(event);
  }

  public autoGroupColumnDef: ColDef = {
    minWidth: 300,
    cellRendererParams: {
      innerRenderer: MyInnerRenderer,
    },
  };

  sortChange(event: any) {
    const sortModel = (this.gridApi as any).sortController?.getSortModel() || [];

    if (sortModel.length === 0) {
      this.gridRowData = [...this.originalRowData];
      this.refreshGridData.emit(this.gridRowData);
    } else {
      this.refreshGridData.emit(
        event.api
          .getModel()
          .rowsToDisplay.map((row: any, index: number) => row.data)
      );
    }
  }

  onRowClicked(event: any) {
    if (this.selectedRowIndex > -1) {
      if (!event.data) {
        event.api.forEachNode((node: any) => {
          if (node.rowIndex === this.selectedRowIndex) {
            node.setSelected(true);
          }
        });

        return;
      }
    }

    this.selectedRowIndex = event.api.getFocusedCell().rowIndex;
    this.onClickedRow.emit(event);
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = this.agGrid.api;
    if (this.gridApi) {
      this.dataLoaded.emit(this.gridApi);
    }
    this.agGrid.api.hidePopupMenu();

    this.agGrid.api.sizeColumnsToFit();
    this.params = params;

    if (this.bypassAutoHeight) {
      this.agGrid.api?.setDomLayout("normal");
    }

    this.totalRecords = this.originalRowData.length;
    this.totalPages = Math.ceil(this.totalRecords / this.pageSize);

    window.onresize = () => {
      this.agGrid.api.sizeColumnsToFit();
    };

    this.dropzoneService.dropzone$
      .pipe(
        map((x: string[]) => x.filter((gridId) => gridId !== this.gridId)),
        untilDestroyed(this)
      )
      .subscribe((dropzones) => {
        this.addDropZone(this.params, dropzones);
      });

    if (this.draggableAndDroppable) {
      this.dropzoneService.setDropzone(this.gridId);
    }

    this.gridService.resize$.pipe(untilDestroyed(this)).subscribe(() => {
      this.agGrid.api.sizeColumnsToFit();
    });

    setTimeout(() => {
      this.agGrid.api.sizeColumnsToFit();
    }, 500);

    if (this.resize$) {
      this.resize$.pipe(untilDestroyed(this)).subscribe(() => {
        this.agGrid.api.sizeColumnsToFit();
      });
    }

    this.filterUpdated$?.pipe(untilDestroyed(this)).subscribe((filterTerm) => {
      this.agGrid.api.setQuickFilter(filterTerm);
      if (this.gridApi.getDisplayedRowCount() === 0) {
        this.gridApi.showNoRowsOverlay();
      } else {
        this.gridApi.hideOverlay();
      }
    });

    this.refreshCells$?.pipe(untilDestroyed(this)).subscribe(() => {
      this.agGrid.api.refreshCells({ force: false, suppressFlash: true });
    });

    this.selectedRowIndex$?.pipe(untilDestroyed(this)).subscribe((index) => {
      this.agGrid.api.forEachNode((node: any) => {
        if (node.rowIndex === index) {
          node.setSelected(true);
        }
      });
    });

    this.resetSelections$?.pipe(untilDestroyed(this)).subscribe(() => {
      this.agGrid.api.deselectAll();
    });

    this.gridReady.emit(this.gridApi);
  }

  onNextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updateGridData();
    }
  }
  onPreviousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updateGridData();
    }
  }
  onPageSizeChange(event: any) {
    this.pageSize = +event.value;
    this.currentPage = 1;
    this.updateGridData();
  }
  onPageClick(event: any) {
    let value = Number(event.target.value);
    if (!isNaN(value)) {
      if (value < 1) {
        value = 1;
      } else if (value > this.totalPages) {
        value = this.totalPages;
      }
      this.currentPage = value;
      this.updateGridData();
    }
  }
  updateGridData() {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.gridRowData = this.originalRowData.slice(startIndex, endIndex);
    this.agGrid?.api?.setRowData(this.gridRowData);
    this.totalRecords = this.originalRowData.length;
    this.totalPages = Math.ceil(this.totalRecords / this.pageSize);
  }
  hasData(): boolean {
    return this.gridRowData.length > 0;
  }
  onBlur(event: KeyboardEvent) {
    if (event.key === "Enter") {
      this.onPageClick(event);
    }
  }
  getRowHeight(params: RowHeightParams): number | undefined | null {
    return params.data.rowHeight;
  }

  addDropZone(params: GridReadyEvent, dropzones: string[]) {
    dropzones.forEach((dropzoneId) => {
      let dropContainer = document.querySelector(
        `[unique-identifier='${dropzoneId}'`
      ) as any;
      var dropZone: RowDropZoneParams = {
        getContainer: () => {
          return dropContainer as any;
        },
        onDragStop: (params) => {
          this.rowAdded.emit({ data: params.node.data, node: dropContainer });
        },
      };
      params.api.addRowDropZone(dropZone);
    });
  }

  checkCheckboxesById(rowIds: number[], property?: any): void {
    if (!this.gridApi || !rowIds.length) {
      return;
    }

    const selectedNodes = this.gridApi.getSelectedNodes();
    this.gridApi.forEachNodeAfterFilterAndSort((node: any) => {
      const rowId = node.data.icdId || node.data[property];
      if (rowIds.includes(rowId)) {
        node.setSelected(true);
      } else if (
        selectedNodes.some((selectedNode) => selectedNode.id === node.id)
      ) {
        node.setSelected(false);
      }
    });

    this.gridApi.refreshCells();
  }

  newlyCheckCheckboxesById(rowIds: number[], property?: any): void {
    if (!this.gridApi || !rowIds.length) {
      return;
    }

    const selectedNodes = this.gridApi.getSelectedNodes();
    this.gridApi.forEachNodeAfterFilterAndSort((node: any) => {
      const rowId = node.data.icdId || node.data[property];
      if (rowIds.includes(rowId)) {
        node.setSelected(true);
      }
    });

    this.gridApi.refreshCells();
  }

  public refreshRow(rowIndex: number, colIds?: string[]): void {
    const node = this.agGrid.api.getDisplayedRowAtIndex(rowIndex);
    if (node) {
      this.agGrid.api.redrawRows({ rowNodes: [node] });
    }
  }

  refreshMultipleRows(rowIndexes: number[], colIds?: string[]): void {
    const rowNodes = rowIndexes
      .map((index) => this.agGrid.api.getDisplayedRowAtIndex(index))
      .filter((node): node is IRowNode => node !== undefined);

    if (rowNodes.length) {
      this.agGrid.api.redrawRows({
        rowNodes: rowNodes,
      });
    }
  }

  addItem(addedItem: any, index: number) {
    this.agGrid.api.applyTransaction({
      add: [addedItem],
      addIndex: index,
    });
  }
  
  public getSortedRows(): any[] {
    const sorted: any[] = [];
    this.agGrid.api.forEachNodeAfterFilterAndSort(node => {
      sorted.push(node.data);
    });
    return sorted;
  }

  private configureColumnsWithExpandBehavior() {
    this.columnDefs = this.columnDefs.map((colDef) => {
      return {
        ...colDef,
        autoHeight: true,
        wrapText: true,
        cellStyle: (params: any) => ({
          ...(typeof colDef.cellStyle === 'function' ? colDef.cellStyle(params) : colDef.cellStyle || {}),
          whiteSpace: params.data.isRowExpanded ? 'normal' : 'nowrap',
          ...(params.data.isRowExpanded && {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: '1.4',
            wordBreak: 'normal',
            display: 'flex',
            alignItems: 'center',
            paddingTop: '4px',
            paddingBottom: '4px'
          })
        }),
      };
    });

    const expandColumn: ColDef = {
      field: 'expand',
      headerName: '',
      minWidth: 50,
      maxWidth: 50,
      cellRenderer: ExpandIconRendererComponent,
      sortable: false,
      filter: false,
      resizable: false,
    };

    this.columnDefs = [expandColumn, ...this.columnDefs];
  }
}